// DocumentUploadBox.jsx
import React from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Button,
  IconButton,
  FormHelperText,
} from "@mui/material";
import { AddOutlined } from "@mui/icons-material";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ChangeCircleOutlinedIcon from "@mui/icons-material/ChangeCircleOutlined";
import { useTranslation } from "react-i18next";
import arrow_down_icon from "../../images/table/Arrow_down.svg";
import DeleteIcon from "@mui/icons-material/Delete"; 

export default function DocumentUploadBox({
  documentType,
  onDocumentTypeChange,
  onFileChange,
  selectedFile,
  fileType,
  preview,
  isUploaded,
  needsReplacement,
  onDownload,
  isSubmitting,
  docId,
  errorType,
  errorFile,
  touchedType,
  touchedFile,
  availableDocTypes,
  usedDocTypes,
  onRemove, // Prop for the delete handler
  hasFile, // Prop to determine if the delete button should be shown
  canAddDocument,
  isFirstDocument, // Add this new prop
}) {
  const errorTextStyles = {
    color: "#d32f2f",
    fontSize: "0.75rem",
    mt: 0.5,
    ml: 1.5,
  };
  const { t } = useTranslation();
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        border: "1px solid #183059",
        borderRadius: "8px",
        padding: 2,
      }}
    >
      <Box
        sx={{
          width: "100%",
          mb: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1,
        }}
      >
        <FormControl
          fullWidth
          error={touchedType && !!errorType}
          sx={{ maxWidth: "300px" }}
        >
          <Select
            value={documentType}
            onChange={onDocumentTypeChange}
            onBlur={() => touchedType(true)}
            disabled={isUploaded && !needsReplacement}
            displayEmpty
            IconComponent={() => (
              <img
                src={arrow_down_icon}
                alt="dropdown"
                style={{
                  width: "20px",
                  height: "20px",
                  marginRight: "12px",
                  pointerEvents: "none",
                }}
              />
            )}
            sx={{
              borderRadius: "8px",
              height: "40px",
              backgroundColor: "#B7BFCC",
              color: "#1F242E",
              "& .MuiSelect-select": {
                padding: "8px 14px",
                display: "flex",
                alignItems: "center",
              },
              "& svg": {
                color: "#1F242E",
              },
            }}
          >
            <MenuItem value="" disabled>
              {t("Select a document type")}
            </MenuItem>
            {availableDocTypes.map((type) => {
              const isUsed =
                usedDocTypes.includes(type) && documentType !== type;
              return (
                <MenuItem key={type} value={type} disabled={isUsed}>
                  {type}
                  {isUsed && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      (Already uploaded)
                    </Typography>
                  )}
                </MenuItem>
              );
            })}
          </Select>
          {touchedType && errorType && (
            <FormHelperText sx={errorTextStyles}>{errorType}</FormHelperText>
          )}
        </FormControl>
        {/* Show Delete button only when no file is uploaded */}
        {!preview && !isFirstDocument && (
          <IconButton
            onClick={onRemove}
            disabled={isSubmitting}
            sx={{
              borderRadius: 2,
              border: "1px solid #414958",
              width: 40,
              height: 40,
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            X {/* Replace DeleteIcon with X text */}
          </IconButton>
        )}
        {preview && (
          <IconButton
            onClick={onDownload}
            disabled={isSubmitting}
            sx={{
              borderRadius: 2,
              border: "1px solid #ccc",
              width: 40,
              height: 40,
              backgroundColor: "#fff",
              "&:hover": {
                backgroundColor: "#f5f5f5",
              },
            }}
          >
            <ArrowDownwardOutlinedIcon sx={{ color: "#1976D2" }} />
          </IconButton>
        )}
      </Box>

      <Box
        sx={{
          width: "100%",
          height: "300px",
          border: errorFile ? "1px dashed #d32f2f" : "",
          borderRadius: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "hidden",
          mb: 1,
        }}
      >
        {preview ? (
          <Box
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            {fileType === "application/pdf" ||
            (selectedFile?.name &&
              selectedFile.name.toLowerCase().endsWith(".pdf")) ? (
              <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
                <object
                  data={preview}
                  type="application/pdf"
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                >
                  <Box sx={{ textAlign: "center", p: 2 }}>
                    <Typography variant="body2" sx={{ mt: 1 }}>
                      PDF Preview Not Available
                    </Typography>
                    <Button
                      variant="contained"
                      size="small"
                      sx={{ mt: 2 }}
                      onClick={() => window.open(preview, "_blank")}
                    >
                      View PDF
                    </Button>
                  </Box>
                </object>
              </Box>
            ) : (
              <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
                <img
                  src={preview}
                  alt="Document preview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                    display: "block",
                    margin: "0 auto",
                  }}
                />
              </Box>
            )}
            {isUploaded && !needsReplacement && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 8,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  padding: "4px 8px",
                  color: "green",
                  borderRadius: "4px",
                }}
              >
                ✓ Uploaded
              </Typography>
            )}
            {needsReplacement && (
              <Typography
                variant="caption"
                sx={{
                  position: "absolute",
                  bottom: 8,
                  left: 0,
                  right: 0,
                  textAlign: "center",
                  backgroundColor: "rgba(255,255,255,0.8)",
                  padding: "4px 8px",
                  color: "orange",
                  borderRadius: "4px",
                }}
              >
                Pending replacement
              </Typography>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              p: 3,
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 2,
              }}
            >
              <AddOutlined sx={{ fontSize: 22 }} />
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t("New Document")}
              </Typography>
            </Box>
            <Button
              variant="contained"
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: "#20A8E0",
                "&:hover": { backgroundColor: "#1565C0" },
                py: 1,
                px: 3,
                color: "#fff",
                fontWeight: "bold",
                fontSize: "14px",
              }}
              startIcon={<ChangeCircleOutlinedIcon sx={{ fontSize: "16px" }} />}
              onClick={() =>
                document.getElementById(`file-upload-${docId}`).click()
              }
            >
              {t("Upload")}
            </Button>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
              {t("Supported formats: Images, PDF (Max 10MB)")}
            </Typography>
            <input
              id={`file-upload-${docId}`}
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={onFileChange}
            />
          </Box>
        )}
      </Box>
      {preview && (
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center", // Center the buttons
            gap: 2, // Add spacing between buttons
            mb: 1,
            mt: 1,
          }}
        >
          <Button
            variant="contained"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              backgroundColor: "#20A8E0",
              "&:hover": { backgroundColor: "#1565C0" },
              py: 1.5,
              px: 3,
              color: "#fff",
              fontWeight: "bold",
              fontSize: "16px",
              minWidth: "120px",
            }}
            startIcon={<ChangeCircleOutlinedIcon sx={{ fontSize: "16px" }} />}
            component="label"
            disabled={isSubmitting}
          >
            Change
            <input
              type="file"
              accept="image/*,application/pdf"
              hidden
              onChange={onFileChange}
            />
          </Button>
          {hasFile && (
            <Button
              variant="contained"
              onClick={onRemove}
              startIcon={<DeleteIcon sx={{ fontSize: "16px" }} />}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: "#EE3B2B",
                "&:hover": { backgroundColor: "#D32F2F" },
                py: 1.5,
                px: 3,
                color: "#fff",
                fontWeight: "bold",
                fontSize: "16px",
                minWidth: "120px",
              }}
            >
              Delete
            </Button>
          )}
        </Box>
      )}
      {touchedFile && errorFile && (
        <FormHelperText sx={errorTextStyles}>{errorFile}</FormHelperText>
      )}
    </Box>
  );
}
